import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Inbox, Loader2, MapPin, Tag, Check, X } from "lucide-react";
import { toast } from "sonner";

const FILTERS = ["open", "discussion", "approved", "fulfilled", "denied", "expired", "all"];
const STATUS_CLS = {
  open: "bg-amber-500/15 text-amber-400 border border-amber-500/30",
  discussion: "bg-purple-500/15 text-purple-400 border border-purple-500/30",
  approved: "bg-sky-500/15 text-sky-400 border border-sky-500/30",
  fulfilled: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
  denied: "bg-rose-500/15 text-rose-400 border border-rose-500/30",
  expired: "bg-muted text-muted-foreground border border-border",
  dismissed: "bg-muted text-muted-foreground border border-border",
};
const STATUS_LABEL = {
  open: "open",
  discussion: "discussion",
  approved: "awaiting payment",
  fulfilled: "active",
  denied: "denied",
  expired: "expired",
  dismissed: "dismissed",
};

export default function ZoneRequestsPanel() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/zone-requests", { params: filter === "all" ? {} : { status: filter } });
      setData(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await api.post(`/admin/zone-requests/${id}/approve`); toast.success("Approved — zone reserved and payment link emailed to the partner"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deny = async (id) => {
    const reason = window.prompt("Optional: add a short reason for the partner (leave blank to skip)") || "";
    try { await api.post(`/admin/zone-requests/${id}/deny`, { reason }); toast.success("Request denied — partner notified"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const claimAction = async (id, action) => {
    const note = action === "approve" ? null : (window.prompt(action === "discuss" ? "Add a note for the partner about what to discuss:" : "Optional: reason for declining (leave blank to skip)") || "");
    try { await api.post(`/admin/zone-requests/${id}/claim-action`, { action, note }); toast.success(action === "approve" ? "Claim approved — complimentary zone activated" : action === "discuss" ? "Marked for discussion — partner notified on the claim screen" : "Claim declined"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="zone-requests-panel">
      <div>
        <h2 className="font-heading text-lg font-bold">Zone requests</h2>
        <p className="text-sm text-muted-foreground">Territory requests from partners. Approve a specific-zone request to reserve it and email the partner a Stripe payment link — the zone activates only after they pay. Deny to notify the partner.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="zone-requests-filters">
        {FILTERS.map((f) => (
          <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)} data-testid={`zone-requests-filter-${f}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}{f !== "all" && data.counts[f] != null && <span className="ml-1.5 opacity-70">{data.counts[f]}</span>}
          </Button>
        ))}
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : data.items.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="zone-requests-empty"><Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><div className="font-heading font-bold">No requests here</div></div>
        : (
          <div className="space-y-3" data-testid="zone-requests-list">
            {data.items.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`zone-request-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {r.type === "founding_claim" ? <Tag className="w-4 h-4 text-emerald-400" /> : r.type === "zone" ? <Tag className="w-4 h-4 text-primary" /> : <MapPin className="w-4 h-4 text-primary" />}
                    <span className="font-medium">{r.type === "founding_claim" ? `Founding zone claim — ${r.zoneName || "zone"}` : r.type === "zone" ? r.zoneName || "Specific zone" : "Area request"}</span>
                    <Badge className={STATUS_CLS[r.status]}>{STATUS_LABEL[r.status] || r.status}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{r.shopName} · {r.contactEmail}</div>
                  {r.type === "area" && (r.bikePath || r.region || r.reason ? (
                    <div className="mt-1.5 space-y-0.5 text-sm" data-testid={`zone-request-area-${r.id}`}>
                      {r.bikePath && <div><span className="text-muted-foreground">Bike Path / Trail:</span> {r.bikePath}</div>}
                      {r.region && <div><span className="text-muted-foreground">Region:</span> {r.region}</div>}
                      {r.reason && <div className="italic text-muted-foreground mt-0.5">"{r.reason}"</div>}
                    </div>
                  ) : (
                    <div className="text-sm mt-1 italic">"{r.areaText}"</div>
                  ))}
                  {r.adminNote && <div className="text-xs mt-1 italic text-purple-300">Note: {r.adminNote}</div>}
                  <div className="text-xs text-muted-foreground mt-1">{(r.createdAt || "").slice(0, 10)}</div>
                </div>
                {(r.status === "open" || r.status === "discussion") && (
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    {r.type === "founding_claim" ? (
                      <>
                        <Button size="sm" onClick={() => claimAction(r.id, "approve")} data-testid={`zone-claim-approve-${r.id}`}><Check className="w-4 h-4 mr-1" />Approve Claim</Button>
                        <Button size="sm" variant="outline" onClick={() => claimAction(r.id, "discuss")} data-testid={`zone-claim-discuss-${r.id}`}>Request Discussion</Button>
                        <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => claimAction(r.id, "decline")} data-testid={`zone-claim-decline-${r.id}`}><X className="w-4 h-4 mr-1" />Decline</Button>
                      </>
                    ) : (
                      <>
                        {r.type === "zone" && <Button size="sm" onClick={() => approve(r.id)} data-testid={`zone-request-approve-${r.id}`}><Check className="w-4 h-4 mr-1" />Approve</Button>}
                        <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => deny(r.id)} data-testid={`zone-request-deny-${r.id}`}><X className="w-4 h-4 mr-1" />Deny</Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
